import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import LandingLayout from "./LandingLayout";
import CustomTrackingLoader from "./CustomTrackingLoader";
import { PLATFORM_BRAND, PLATFORM_PHONE, PARENT_COMPANY } from "./config";
import { fireLeadConversion, fetchPublicPixels, injectAllPixels } from "@/lib/pixelTracking";
import { pixelCheckNonce, reportPixelCheck } from "@/lib/pixelCheck";
import { emitPreviewEngagement } from "@/lib/prospectEngagement";
import { pickAccent } from "./caseFunnel/caseContent";
import { CaseThankYou } from "./thankYou/CaseThankYou";
import { BuyerMessage, DEFAULT_TY, FirmBlock, NextSteps } from "./thankYou/ThankYouParts";
import { ThankYouTestimonials } from "./BuyerTestimonials";
import { BlockThankYou } from "./thankYou/ThankYouBlocks";
import { useSharedSections } from "@/lib/useSharedSections";
import { useTyClickStats, visitorId } from "@/lib/tyStats";
import { fillFirm } from "@/lib/sharedSectionDefaults";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

const CHANNEL_LABEL = {
  call:  { label: "Call",  icon: "📞", detail: "You'll get a call within the hour" },
  text:  { label: "Text",  icon: "💬", detail: "Watch for a text at the number you gave us" },
  email: { label: "Email", icon: "✉️", detail: "Check your inbox (and spam folder just in case)" },
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const isTest = params.get("test") === "1";
  const leadId = params.get("lead_id");
  // Iter-43: buyer-branded submissions forward `firm` + `phone`.
  const firmQP = params.get("firm");
  const phoneQP = params.get("phone");
  // Iter-44: `slug` allows fetching admin-authored thank-you copy.
  const slug = params.get("slug");
  // Global funnels: `funnel` is the shared page, `b` the buyer it was bound to.
  const funnelQP = params.get("funnel");
  const buyerQP = params.get("b");
  const fetchSlug = funnelQP || slug;
  // Iter-50: client-share preview token — lets a buyer view the Thank-You
  // page for a draft/paused page without submitting a real lead.
  const demoToken = params.get("demo") || "";
  const isDemo = Boolean(demoToken);
  // Iter-57: `experience` query param tells us to render the compact
  // Focused Funnel TY variant instead of the classic channels grid.
  // Backwards-compatible — omitted on Standard/Quiz submissions so the
  // existing full-featured TY keeps rendering unchanged.
  const experienceQP = (params.get("experience") || "").toLowerCase();
  const previewQP = params.get("preview") === "1";
  // Preview/demo only: try the thank-you page in another layout before saving it.
  const tryDesignQP = previewQP || demoToken ? (params.get("ty_design") || "") : "";

  const [lp, setLp] = useState(null);
  const [lpFailed, setLpFailed] = useState(false);
  const checkNonce = pixelCheckNonce();
  const shared = useSharedSections();
  const [defaultDesign, setDefaultDesign] = useState(undefined);
  const [tyTest, setTyTest] = useState(null);
  useEffect(() => {
    if (fetchSlug) return;
    axios.get(`${API}/public/thankyou-default`, { params: { vid: visitorId() || undefined, b: buyerQP || undefined } })
      .then((r) => { setDefaultDesign(r.data?.design || null); setTyTest(r.data?.test || null); })
      .catch(() => setDefaultDesign(null));
  }, [fetchSlug, buyerQP]);

  useEffect(() => {
    if (!fetchSlug) return;
    let cancelled = false;
    const qs = new URLSearchParams();
    if (demoToken) qs.set("demo", demoToken);
    if (funnelQP && buyerQP) qs.set("b", buyerQP);
    if (previewQP) qs.set("preview", "1");
    if (tryDesignQP && tryDesignQP !== "classic") qs.set("ty_design", tryDesignQP);
    const vid = visitorId();
    if (vid) qs.set("vid", vid);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    axios
      .get(`${API}/public/custom-landing/${encodeURIComponent(fetchSlug)}${suffix}`)
      .then((r) => { if (!cancelled) setLp(r.data); })
      .catch(() => { if (!cancelled) setLpFailed(true); });
    return () => { cancelled = true; };
  }, [fetchSlug, funnelQP, buyerQP, demoToken, previewQP, tryDesignQP]);

  // Iter-45: fire the standard "Lead" conversion event on every
  // configured pixel provider (Meta / Google Ads / GA4 / TikTok) once
  // the LP config is loaded. Skipped for sandbox / test / demo
  // submissions so we don't pollute the ad platforms with fake
  // conversions.
  //
  // Iter-45.2: When there's no custom-LP slug (hardcoded vertical flow),
  // fall back to the platform-wide pixel config so first-party ads still
  // record conversions.
  useEffect(() => {
    if (checkNonce) {
      if (lp) reportPixelCheck(checkNonce, "thank_you", fetchSlug, lp.is_sample_page ? [] : [lp.pixels, lp.buyer_pixels]);
      else if (!fetchSlug || lpFailed) fetchPublicPixels().then((cfg) => reportPixelCheck(checkNonce, "thank_you", fetchSlug, [cfg]));
      return;
    }
    if (isTest || isDemo || previewQP) return;
    // Iter-54.1: demo submissions come back with a "DEMO-XXXXXXXX"
    // reference — never fire Lead conversion pixels for them, even if
    // the LP config forgot to disable pixels.
    if (leadId && leadId.toUpperCase().startsWith("DEMO-")) return;
    if (lp?.is_sample_page) return;
    if (lp?.pixels) {
      const ctx = { reference: leadId ? leadId.slice(0, 8).toUpperCase() : undefined };
      // Main company pixels load here too, so a direct thank-you visit still counts.
      injectAllPixels(lp.pixels);
      fireLeadConversion(lp.pixels, ctx);
      // Buyer's own pixels fire in addition to the global set.
      if (lp.buyer_pixels) {
        injectAllPixels(lp.buyer_pixels);
        fireLeadConversion(lp.buyer_pixels, ctx);
      }
      return;
    }
    // Page settings didn't load: still fire the main pixels.
    if (fetchSlug && !lpFailed) return;  // custom LP still resolving — skip; useEffect will re-run
    let cancelled = false;
    fetchPublicPixels().then((cfg) => {
      if (cancelled || !cfg) return;
      injectAllPixels(cfg);
      fireLeadConversion(cfg, {
        reference: leadId ? leadId.slice(0, 8).toUpperCase() : undefined,
      });
    });
    return () => { cancelled = true; };
  }, [lp, lpFailed, isTest, isDemo, leadId, fetchSlug, previewQP, checkNonce]);

  // Iter-63.1: prospect-share thank-you view. Only fires when the visitor
  // arrived at the Thank You surface via a share preview link
  // (`?demo=<token>`). Never runs on real submissions.
  useEffect(() => {
    if (!isDemo || !demoToken || !slug) return;
    if (lp?.is_sample_page) return;
    emitPreviewEngagement(demoToken, slug, "thank_you_view", {
      isSamplePage: lp?.is_sample_page,
      dedupe: `${demoToken}:${slug}:thank_you_view`,
    });
    emitPreviewEngagement(demoToken, slug, "form_complete_preview", {
      isSamplePage: lp?.is_sample_page,
      dedupe: `${demoToken}:${slug}:form_complete_preview`,
    });
  }, [isDemo, demoToken, slug, lp?.is_sample_page]);

  // Merge admin config with URL params; URL params take precedence for
  // firm/phone since they're already stamped on the lead audit trail.
  const firm = firmQP || lp?.firm_name || null;
  // A buyer's separate thank-you (intake) number wins over their main number.
  const phone = lp?.thankyou_phone || phoneQP || lp?.phone || (firm ? null : PLATFORM_PHONE);
  const displayPhone = phone || PLATFORM_PHONE;
  // Iter-53: honour the per-LP + global phone-display kill-switch. Backend
  // returns `effective_hide_phone` in the /public/custom-landing/{slug}
  // payload so both toggles roll into one boolean here.
  const hidePhone = Boolean(lp?.effective_hide_phone_ty ?? lp?.effective_hide_phone);
  // Admin switches for what this thank-you page shows.
  const ty = lp?.thankyou_effective || DEFAULT_TY;
  const shownFirm = ty.display.show_firm_name ? firm : null;
  const legalCode = shownFirm ? (buyerQP || slug || null) : null;
  const logoUrl = lp?.logo_url ? `${BACKEND}${lp.logo_url}` : null;
  const showCall = !hidePhone && ty.display.show_call_text && Boolean(phone);
  const buyerMessage = ty.display.show_buyer_message ? (lp?.thankyou_message || null) : null;
  const reference = leadId
    ? leadId.slice(0, 8).toUpperCase()
    : (isDemo ? "DEMO-" + (slug || "PREVIEW").slice(0, 6).toUpperCase() : null);
  const tracking = (
    <>
      {fetchSlug && !isTest && !isDemo && !previewQP && !checkNonce && lp && !lp.is_sample_page && (
        <CustomTrackingLoader
          slug={fetchSlug}
          isPreviewSurface={false}
          isSample={Boolean(lp?.is_sample_page)}
          surface="thank_you"
          context={{
            landing_page_slug: fetchSlug,
            landing_page_id: lp?.id || null,
            event: "lead_submitted",
            session_id: null,
          }}
        />
      )}
    </>
  );

  // Iter-54.2c: when a firm= URL param is present alongside a preview
  // surface (?preview=1 or demo=<token>), it means the operator is
  // pitching this LP to a specific prospective buyer — the personalised
  // firm name should win over the admin-configured thankyou_headline
  // so every screen the prospect sees (header, form title, phone bar,
  // TY headline, TY CTA) speaks the same brand. On real production
  // traffic (no preview surface), the admin's page-specific headline
  // still wins as before.
  const isDemoOrPreview = isDemo || params.get("preview") === "1";
  const headline =
    (firmQP && isDemoOrPreview && ty.display.show_firm_name)
      ? `Thank you — ${firmQP} has your case`
      : (lp?.thankyou_headline ||
         (shownFirm ? `Thank you — ${shownFirm} has your case` : "Thank you — we received your case"));

  const message =
    (firmQP && isDemoOrPreview)
      ? `A member of ${firmQP}'s legal intake team will reach out shortly at the number you provided. ${PLATFORM_BRAND} (a ${PARENT_COMPANY} company) has forwarded your information — ${firmQP} is now your point of contact for the case. Please keep an eye out so you don't miss their call.`
      : (buyerMessage ||
         (shownFirm
           ? `A member of ${shownFirm}'s legal intake team will reach out shortly at the number you provided. ${PLATFORM_BRAND} (a ${PARENT_COMPANY} company) has forwarded your information — ${shownFirm} is now your point of contact for the case. Please keep an eye out so you don't miss their call.`
           : `A member of our legal intake team will call you shortly at the number you provided. ${PLATFORM_BRAND} — a ${PARENT_COMPANY} company — will connect you with an attorney in your state.`));

  // Default to all three channels when the LP didn't specify any.
  const channels = (lp?.thankyou_channels && lp.thankyou_channels.length)
    ? lp.thankyou_channels
    : ["call", "text", "email"];

  // Iter-57: prefer the compact Focused Funnel TY when either the query
  // param OR the resolved LP config says so. Falls back to the classic
  // full-featured TY for Standard/Quiz — those experiences continue to
  // render unchanged.
  const isFocused =
    experienceQP === "focused_funnel"
    || (lp?.form_experience === "focused_funnel");

  const forceClassic = isDemoOrPreview && params.get("ty_design") === "classic";
  const design = forceClassic ? null : (fetchSlug ? lp?.thankyou_design_resolved : defaultDesign);
  useTyClickStats({
    ready: fetchSlug ? Boolean(lp) : defaultDesign !== undefined,
    design: design?.key || "classic",
    buyer: buyerQP || slug,
    test: fetchSlug ? lp?.thankyou_test_run : tyTest,
    skip: isTest || isDemoOrPreview || Boolean(lp?.is_sample_page),
  });
  const isCaseTy = experienceQP === "case_quiz" || lp?.form_experience === "case_quiz";
  const heroBlock = (design?.blocks || []).find((b) => b.type === "hero" && b.enabled !== false);
  const shownHeadline = design?.blocks?.length ? (heroBlock?.headline ? fillFirm(heroBlock.headline, shownFirm, "our team") : headline)
    : isCaseTy ? (lp?.thankyou_headline || (shownFirm ? `You're matched with ${shownFirm}` : "Your case request is in"))
      : isFocused ? (shownFirm ? `You're all set — ${shownFirm} has your info` : "You're all set") : headline;
  const tyReady = fetchSlug ? Boolean(lp) || lpFailed : defaultDesign !== undefined;
  // Record what this visitor's thank-you page showed, so admins can see it on the lead.
  useEffect(() => {
    if (!tyReady || !leadId || isTest || isDemoOrPreview || checkNonce || leadId.toUpperCase().startsWith("DEMO-")) return;
    const key = `ty-shown-${leadId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* storage blocked: still report once per load */ }
    axios.post(`${API}/public/leads/${encodeURIComponent(leadId)}/thankyou-shown`, {
      firm: shownFirm || null, phone: showCall ? phone : null, headline: shownHeadline,
      design: design?.key || (isCaseTy ? "case" : isFocused ? "focused" : "classic"),
      buyer_code: buyerQP || slug || null, page: fetchSlug || null,
      url: `${window.location.pathname}${window.location.search}`,
    }).catch(() => {});
  }, [tyReady, leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (design?.blocks?.length) {
    return (
      <LandingLayout brand={shownFirm} hidePhone={!showCall} brandPhone={phone} buyerCode={legalCode}>
        {tracking}
        <BlockThankYou
          blocks={design.blocks}
          ctx={{
            firm: shownFirm, logoUrl: ty.display.show_logo ? logoUrl : null, phone: displayPhone, showCall,
            reference, buyerCode: buyerQP || slug, contact: lp?.thankyou_contact, videoUrl: lp?.thankyou_video_url,
            timeline: ty.timeline, headline, message, accent: lp?.brand_color || "#0a1929", shared,
            platformName: PLATFORM_BRAND, isTest,
          }}
        />
      </LandingLayout>
    );
  }

  const isCase = experienceQP === "case_quiz" || lp?.form_experience === "case_quiz";
  if (isCase) {
    return (
      <CaseThankYou
        ty={ty}
        firm={shownFirm}
        buyerCode={legalCode}
        logoUrl={logoUrl}
        phone={phone}
        showCall={showCall}
        buyerMessage={buyerMessage}
        customHeadline={lp?.thankyou_headline}
        accent={pickAccent(lp?.brand_color, funnelQP)}
        reference={reference}
        isTest={isTest}
        isDemo={isDemo}
        tracking={tracking}
      />
    );
  }

  if (isFocused) {
    return (
      <LandingLayout brand={shownFirm} hidePhone={!showCall} brandPhone={phone} buyerCode={legalCode}>
        {tracking}
        <div className="ff-thankyou" data-testid="lp-thankyou-focused">
          {isDemo && (
            <div
              data-testid="lp-thankyou-demo-banner"
              style={{
                background: "#0f172a", color: "#f8fafc",
                padding: "8px 16px", margin: "0 auto 24px",
                maxWidth: 560,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, letterSpacing: "0.12em",
                textAlign: "center", textTransform: "uppercase",
                border: "1px solid #000", fontWeight: 700,
              }}
            >
              Private preview — this is what a real lead sees after submitting
            </div>
          )}
          <div className="ff-thankyou-badge">✓</div>
          <FirmBlock logoUrl={logoUrl} firm={shownFirm} display={ty.display} />
          <h1 data-testid="lp-thankyou-headline">
            {firmQP && isDemoOrPreview && ty.display.show_firm_name
              ? `You're all set — ${firmQP} has your info`
              : (shownFirm ? `You're all set — ${shownFirm} has your info` : "You're all set")}
          </h1>
          <p data-testid="lp-thankyou-message">
            {shownFirm ? `A member of ${shownFirm}'s intake team may contact you shortly.`
                  : "A member of our intake team may contact you shortly."}
          </p>
          {showCall && displayPhone && (
            <>
              <a
                href={`tel:${displayPhone.replace(/[^0-9+]/g, "")}`}
                className="ff-thankyou-cta"
                data-testid="lp-thankyou-call-btn"
              >Call {shownFirm || "our team"} · {displayPhone}</a>
              <a
                href={`sms:${displayPhone.replace(/[^0-9+]/g, "")}`}
                className="ff-thankyou-cta"
                style={{ marginTop: 10 }}
                data-testid="lp-thankyou-text-btn"
              >Text {shownFirm || "our team"}</a>
            </>
          )}
          {buyerMessage && <BuyerMessage text={buyerMessage} firm={shownFirm} />}
          {ty.display.show_timeline && <NextSteps steps={ty.timeline} />}
          <ThankYouTestimonials code={buyerQP || slug} firm={shownFirm} />
          {(leadId || isDemo) && (
            <div
              data-testid="lp-thankyou-reference"
              style={{
                marginTop: 20,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: "#64748b",
                letterSpacing: "0.05em",
              }}
            >
              Reference #: {leadId
                ? leadId.slice(0, 8).toUpperCase()
                : "DEMO-" + (slug || "PREVIEW").slice(0, 6).toUpperCase()}
            </div>
          )}
          {isTest && (
            <div className="lp-sandbox-banner" style={{ maxWidth: 420, margin: "24px auto 0" }}>
              SANDBOX SUBMISSION — logged as a test lead (id: {leadId?.slice(0, 8)}…)
            </div>
          )}
        </div>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout brand={shownFirm} hidePhone={!showCall} brandPhone={phone} buyerCode={legalCode}>
      {/* Iter-56: THANK_YOU_ONLY triggers; the loader skips preview/demo/sample. */}
      {tracking}
      <div className="lp-thankyou" data-testid="lp-thankyou">
        {isDemo && (
          <div
            data-testid="lp-thankyou-demo-banner"
            style={{
              background: "#0f172a", color: "#f8fafc",
              padding: "8px 16px", margin: "0 auto 24px",
              maxWidth: 720,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, letterSpacing: "0.12em",
              textAlign: "center", textTransform: "uppercase",
              border: "1px solid #000", fontWeight: 700,
            }}
          >
            Private preview — this is what a real lead sees after submitting
          </div>
        )}
        <div className="lp-thankyou-badge">✓</div>
        <FirmBlock logoUrl={logoUrl} firm={shownFirm} display={ty.display} />
        <h1 data-testid="lp-thankyou-headline">{headline}</h1>

        <p style={{ maxWidth: 640, margin: "12px auto 0", lineHeight: 1.6 }}
           data-testid="lp-thankyou-message">
          {message}
        </p>

        {firm && (() => {
          // Iter-63.1.9: responsive channels grid. Old inline layout
          // hard-coded `repeat(3, minmax(140px, 1fr))` which needed at
          // least 444 px (3*140 + 2*12) and clipped on any phone
          // under ~460 px (iPhone SE / 12 / 14 / 15 / 15 Pro are all
          // 375-393 px). Stack to one column on mobile, two on small
          // tablets, up to three on desktop.
          const n = Math.min(channels.length, 3);
          const cols =
            n >= 3 ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3" :
            n === 2 ? "grid-cols-1 sm:grid-cols-2" :
            "grid-cols-1";
          return (
            <div
              className={`lp-thankyou-channels grid ${cols} gap-3 mx-auto mt-7`}
              data-testid="lp-thankyou-channels"
              style={{ maxWidth: 720, width: "100%" }}
            >
              {channels.map((c) => {
                const cfg = CHANNEL_LABEL[c];
                if (!cfg) return null;
                return (
                  <div
                    key={c}
                    data-testid={`lp-thankyou-channel-${c}`}
                    style={{
                      border: "2px solid #0a1929",
                      padding: "14px 12px",
                      background: "#fff",
                      textAlign: "left",
                      boxShadow: "3px 3px 0 0 #0a1929",
                      minWidth: 0,
                    }}
                  >
                    <div style={{ fontSize: 22, lineHeight: 1 }}>{cfg.icon}</div>
                    <div style={{
                      fontFamily: "'Chivo', system-ui, sans-serif",
                      fontWeight: 900,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      fontSize: 12,
                      marginTop: 6,
                    }}>
                      {cfg.label}
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: "#475569",
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}>
                      {cfg.detail}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {ty.display.show_timeline && <NextSteps steps={ty.timeline} />}
        <ThankYouTestimonials code={buyerQP || slug} firm={shownFirm} />

        {showCall && (
        <a
          href={`tel:${displayPhone.replace(/[^0-9+]/g, "")}`}
          className="lp-cta"
          data-testid="lp-thankyou-call-btn"
          style={{
            maxWidth: 360,
            display: "inline-block",
            textAlign: "center",
            textDecoration: "none",
            marginTop: 24,
          }}
        >
          📞 Call {shownFirm || "our team"} at {displayPhone}
        </a>
        )}
        {showCall && (
          <a
            href={`sms:${displayPhone.replace(/[^0-9+]/g, "")}`}
            className="lp-btn-ghost"
            data-testid="lp-thankyou-text-btn"
            style={{ display: "block", maxWidth: 360, margin: "12px auto 0", textAlign: "center" }}
          >
            Text {shownFirm || "our team"} instead
          </a>
        )}

        {(leadId || isDemo) && (
          <div
            data-testid="lp-thankyou-reference"
            style={{
              marginTop: 20,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              color: "#64748b",
              letterSpacing: "0.05em",
            }}
          >
            Reference #: {leadId
              ? leadId.slice(0, 8).toUpperCase()
              : "DEMO-" + (slug || "PREVIEW").slice(0, 6).toUpperCase()}
          </div>
        )}

        {isTest && (
          <div className="lp-sandbox-banner" style={{ maxWidth: 420, margin: "24px auto 0" }}>
            SANDBOX SUBMISSION — logged as a test lead (id: {leadId?.slice(0, 8)}…)
          </div>
        )}

        {!firm && (
          <p style={{ marginTop: 32, fontSize: 13 }}>
            Not what you were looking for?{" "}
            <a href="/lp/auto-accident" style={{ color: "#0a1929" }}>
              Return to the case-review form
            </a>
            .
          </p>
        )}
      </div>
    </LandingLayout>
  );
}
